import DebouncedResizeContainer from "@/components/DebouncedResizeContainer";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBackendApi } from "@/hooks/fetch";
import { dateToLocaleDateString, dateToLocaleDateTimeString } from "@/lib/dateTime";
import { UserRoundPlusIcon, UsersIcon } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import useSWR from "swr";
import type { PlayerCountApiResp, PlayerCountApiSuccessResp } from "@shared/otherTypes";
import { PlayerDropsLoadingSpinner, PlayerDropsMessage } from "./PlayerDropsGenericSubcards";
import PlayerCountChart from "./PlayerCountChart";
import NewPlayersChart from "./NewPlayersChart";
import type { PlayerCountDatum } from "./drawPlayerCount";
import type { NewPlayersDatum } from "./drawNewPlayers";


const hourMs = 60 * 60 * 1000;
const dayMs = 24 * hourMs;

//Same windows the sanction stats page offers
const ALL_TIME = 'all';
const windowOptions = [
    { value: '7', label: '7 Days' },
    { value: '14', label: '14 Days' },
    { value: '30', label: '30 Days' },
    { value: '90', label: '90 Days' },
    { value: ALL_TIME, label: 'All Time' },
];
const defaultWindow = '14';


//same chip the sanction stats card puts in its header
function LegendChip({ className, label }: { className: string, label: string }) {
    return (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
            <span className={cn('size-2.5 rounded-full', className)} />
            {label}
        </span>
    );
}


function PlayerCountCard() {
    const [countChartSize, setCountChartSize] = useState({ width: 0, height: 0 });
    const [newChartSize, setNewChartSize] = useState({ width: 0, height: 0 });
    const [windowDays, setWindowDays] = useState(defaultWindow);
    const [hoveredCount, setHoveredCount] = useState<PlayerCountDatum | undefined>(undefined);
    const [hoveredNew, setHoveredNew] = useState<NewPlayersDatum | undefined>(undefined);
    const onHoverCount = useCallback((datum: PlayerCountDatum | undefined) => setHoveredCount(datum), []);
    const onHoverNew = useCallback((datum: NewPlayersDatum | undefined) => setHoveredNew(datum), []);

    //the charts are redrawn without a mouseleave, so the old readout would stick around
    const changeWindow = useCallback((value: string) => {
        setWindowDays(value);
        setHoveredCount(undefined);
        setHoveredNew(undefined);
    }, []);

    const playerCountApi = useBackendApi<PlayerCountApiResp>({
        method: 'GET',
        path: `/playerCountData`,
    });
    const swrResp = useSWR('/playerCountData', async () => {
        const data = await playerCountApi({});
        if (!data) throw new Error('empty_response');
        if ('fail_reason' in data) throw new Error(data.fail_reason);
        return data as PlayerCountApiSuccessResp;
    }, {
        revalidateOnFocus: false,
    });

    const chartData = useMemo(() => {
        const log = swrResp.data?.log;
        if (!log?.length) return;
        const joins = swrResp.data?.newPlayers ?? [];

        //the counts only exist since we started recording, but the joins go back as
        //far as the server does, so all time is whichever of the two starts first
        const rawLast = Date.now();
        const rawFirst = windowDays === ALL_TIME
            ? Math.min(log[0].hourTs, joins.length ? joins[0].hourTs : Infinity)
            : rawLast - (parseInt(windowDays) * dayMs);

        //an hourly chart spanning months is unreadable, so longer spans go per day or week
        const spanDays = (rawLast - rawFirst) / dayMs;
        const bucketKind = spanDays > 120 ? 'week' : spanDays > 14 ? 'day' : 'hour';
        const bucketMs = bucketKind === 'week' ? 7 * dayMs : bucketKind === 'day' ? dayMs : hourMs;
        const bucketStart = (ts: number) => {
            if (bucketKind === 'hour') return Math.floor(ts / hourMs) * hourMs;
            const date = new Date(ts);
            date.setHours(0, 0, 0, 0);
            if (bucketKind === 'week') date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
            return date.getTime();
        };
        const nextBucket = (ts: number) => {
            if (bucketKind === 'hour') return ts + hourMs;
            const date = new Date(ts);
            date.setDate(date.getDate() + (bucketKind === 'week' ? 7 : 1));
            date.setHours(0, 0, 0, 0);
            return date.getTime();
        };

        const folded = new Map<number, { min: number; max: number; sum: number; count: number }>();
        for (const hour of log) {
            if (hour.hourTs < rawFirst) continue;
            const bucketTs = bucketStart(hour.hourTs);
            const bucket = folded.get(bucketTs);
            if (bucket) {
                bucket.min = Math.min(bucket.min, hour.min);
                bucket.max = Math.max(bucket.max, hour.max);
                bucket.sum += hour.avg;
                bucket.count++;
            } else {
                folded.set(bucketTs, { min: hour.min, max: hour.max, sum: hour.avg, count: 1 });
            }
        }

        //no samples means nobody was online, so the whole span is drawn even where
        //there is nothing recorded for it
        const lastTs = bucketStart(rawLast);
        const series: PlayerCountDatum[] = [];
        for (let ts = bucketStart(rawFirst); ts <= lastTs; ts = nextBucket(ts)) {
            const bucket = folded.get(ts);
            series.push({
                date: new Date(ts),
                min: bucket?.min ?? 0,
                avg: bucket ? Math.round(bucket.sum / bucket.count) : 0,
                max: bucket?.max ?? 0,
            });
        }

        //both charts share the same axis, so they can be read against each other
        const domain: [Date, Date] = [series[0].date, series[series.length - 1].date];
        const joinsPerBucket = new Map<number, number>();
        for (const hour of joins) {
            if (hour.hourTs < domain[0].getTime()) continue;
            const bucketTs = bucketStart(hour.hourTs);
            joinsPerBucket.set(bucketTs, (joinsPerBucket.get(bucketTs) ?? 0) + hour.count);
        }
        const newPlayers: NewPlayersDatum[] = series.map(({ date }) => ({
            date,
            count: joinsPerBucket.get(date.getTime()) ?? 0,
        }));

        return { series, newPlayers, domain, bucketKind, bucketMs };
    }, [swrResp.data, windowDays]);

    const formatBucket = (date: Date) => {
        if (chartData?.bucketKind === 'week') return `week of ${dateToLocaleDateString(date, 'medium')}`;
        if (chartData?.bucketKind === 'day') return dateToLocaleDateString(date, 'medium');
        return dateToLocaleDateTimeString(date, 'short', 'short');
    };
    const bucketNoun = chartData?.bucketKind === 'week'
        ? 'week'
        : chartData?.bucketKind === 'day' ? 'day' : 'hour';
    const chartMessage = swrResp.error ? (
        <PlayerDropsMessage message={
            swrResp.error.message === 'not_enough_data'
                ? 'Not enough data collected yet.'
                : `Failed to load: ${swrResp.error.message}`
        } />
    ) : (
        <PlayerDropsLoadingSpinner />
    );

    return (
        <div className="w-full space-y-4 md:space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-2 px-2 md:px-0">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <UsersIcon className="size-4" />
                    <span className="text-sm">
                        {windowDays === ALL_TIME
                            ? 'Player activity over the full history'
                            : `Player activity in the last ${windowDays} days`}
                    </span>
                </div>
                <Tabs value={windowDays} onValueChange={changeWindow}>
                    <TabsList>
                        {windowOptions.map((option) => (
                            <TabsTrigger key={option.value} value={option.value}>
                                {option.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            </div>

            <div className="md:rounded-hud border-edge bg-card shadow-surface flex flex-col">
                <div className="flex flex-row items-center justify-between gap-2 px-1 sm:px-4 border-b rounded-t-[inherit] bg-secondary/35">
                    <div className="flex items-center py-2 space-x-2 shrink-0">
                        <div className='hidden xs:block'><UsersIcon className="size-4" /></div>
                        <h2 className="font-mono text-sm">Players Online</h2>
                    </div>
                    {/* fixed slot, so nothing jumps when the readout replaces the legend */}
                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 py-2 min-w-0">
                        {hoveredCount ? (
                            <span className="font-mono text-xs text-muted-foreground truncate">
                                {`${formatBucket(hoveredCount.date)} · ø ${hoveredCount.avg} · max ${hoveredCount.max} · min ${hoveredCount.min}`}
                            </span>
                        ) : (<>
                            <LegendChip className="bg-accent" label={`Average per ${bucketNoun}`} />
                            <LegendChip
                                className="bg-accent/20"
                                label={chartData?.bucketKind === 'hour' ? 'Quietest to busiest minute' : 'Up to the busiest minute'}
                            />
                        </>)}
                    </div>
                </div>

                <div className="h-64 max-h-64">
                    {chartData ? (
                        <DebouncedResizeContainer onDebouncedResize={setCountChartSize}>
                            <PlayerCountChart
                                data={chartData.series}
                                domain={chartData.domain}
                                bucketMs={chartData.bucketMs}
                                width={countChartSize.width}
                                height={countChartSize.height}
                                onHover={onHoverCount}
                            />
                        </DebouncedResizeContainer>
                    ) : chartMessage}
                </div>
            </div>

            <div className="md:rounded-hud border-edge bg-card shadow-surface flex flex-col">
                <div className="flex flex-row items-center justify-between gap-2 px-1 sm:px-4 border-b rounded-t-[inherit] bg-secondary/35">
                    <div className="flex items-center py-2 space-x-2 shrink-0">
                        <div className='hidden xs:block'><UserRoundPlusIcon className="size-4" /></div>
                        <h2 className="font-mono text-sm">New Players</h2>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 py-2 min-w-0">
                        {hoveredNew ? (
                            <span className="font-mono text-xs text-muted-foreground truncate">
                                {`${formatBucket(hoveredNew.date)} · ${hoveredNew.count} new`}
                            </span>
                        ) : (
                            <LegendChip className="bg-accent" label={`First time joins per ${bucketNoun}`} />
                        )}
                    </div>
                </div>

                <div className="h-64 max-h-64">
                    {chartData ? (
                        <DebouncedResizeContainer onDebouncedResize={setNewChartSize}>
                            <NewPlayersChart
                                data={chartData.newPlayers}
                                domain={chartData.domain}
                                bucketMs={chartData.bucketMs}
                                width={newChartSize.width}
                                height={newChartSize.height}
                                onHover={onHoverNew}
                            />
                        </DebouncedResizeContainer>
                    ) : chartMessage}
                </div>

            </div>
        </div>
    );
}

export default memo(PlayerCountCard);
